import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DotLottleLoader } from '../../../components/Loader';
import axiosInstant from '../../../api';
import { useUser } from '../../../hooks/useUser';
import ExpertSelectionCard from '../../videoProduction/components/ExpertSelectionCard';
import { IBrandExpert } from '../../brand/expertsV2/expertListView/constants';
import { usePortalPath } from '../../../hooks/usePortalPath'

interface FeaturedExpertsProps {
  limit?: number;
  onCount?: (count: number) => void;
}

const FeaturedExperts: React.FC<FeaturedExpertsProps> = ({ limit = 8, onCount }) => {
  const navigate = useNavigate();
  const { p } = usePortalPath();
  const { user } = useUser();
  const [experts, setExperts] = useState<IBrandExpert[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    const fetchFeaturedExperts = async () => {
      try {
        setLoading(true);
        const response = await axiosInstant.get('/users/pros', {
          params: { featured: 'true', offset: 0, limit },
        });
        const rows = response.data?.rows || response.data?.data || [];
        const count = response.data?.count || 0;
        setExperts(rows);
        setTotalCount(count);
        onCount?.(count);
      } catch (error) {
        console.error('Error fetching featured experts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedExperts();
  }, [user, limit]);

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-full min-h-0">
        <DotLottleLoader fill={false} />
      </Box>
    );
  }

  if (experts.length === 0) return null;

  return (
    <Box className="flex flex-col h-full min-h-0">
      <Box className="flex items-center justify-between mb-4 flex-shrink-0">
        <Typography
          sx={{
            color: 'var(--text)',
            fontFamily: 'CircularStd',
            fontSize: { xs: '15px', md: '18px' },
            fontWeight: 700,
          }}
        >
          Featured Experts ({totalCount})
        </Typography>
        <Button
          onClick={() => navigate(p('/experts/featured'))}
          sx={{
            color: 'var(--accent-text)',
            fontFamily: 'CircularStd',
            fontSize: { xs: '12px', md: '14px' },
            fontWeight: 450,
            textTransform: 'none',
            padding: 0,
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          View All
        </Button>
      </Box>

      <Box className="flex-1 min-h-0 overflow-x-auto pb-0">
        <Box className="flex gap-6 h-full max-md:gap-4" style={{ width: 'max-content' }}>
          {experts.map((expert) => (
            <div
              key={expert.userid}
              className="flex-shrink-0 w-[300px] max-md:!w-[230px] [&>div]:max-md:!min-w-0"
            >
              <ExpertSelectionCard
                isSelected={false}
                recommendationCheck={false}
                pro={expert}
                onUpdate={() => {}}
                project={null}
                onScoreChange={() => {}}
                approvalCheck={false}
                isTopRecommendation={false}
                isApprovalPage={false}
                hideKeyPoints={true}
                hideSelection={true}
                customHeight={302 as any}
              />
            </div>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default FeaturedExperts;
